/**
 * SourceFreshness
 *
 * Says two different things that are easily confused: when the source last
 * published, and when we last looked.
 *
 * Farmgate pulls CBN once a day, and CBN publishes on business days with
 * gaps. So the newest rate is routinely a day or three old while everything
 * is working perfectly. Showing only the observation date makes that look
 * like a fault, and the question "why has this not updated" gets asked every
 * quiet week. Showing both answers it before it is asked.
 *
 * Renders nothing without a real date, like UpdatedStamp: an invented
 * timestamp is worse than none.
 */

import React from 'react';
import { Tooltip, Typography } from '@mui/material';
import { useThemeMode } from '../../context/ThemeContext';
import { fmtDate } from '../../utils/format';

/** "just now", "3 hours ago", "yesterday" - vague on purpose, since the
 *  precise minute we polled is not information anybody needs. */
function ago(iso) {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const mins = Math.floor((Date.now() - then.getTime()) / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins} minutes ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return hours === 1 ? 'an hour ago' : `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}

function SourceFreshness({ date, checkedAt, source = 'CBN', component = 'p', sx }) {
  const { palette } = useThemeMode();
  if (!date) return null;
  const label = fmtDate(date);
  if (label === '-') return null;

  const checked = ago(checkedAt);
  const text = checked
    ? `${source} last published ${label} · checked ${checked}`
    : `${source} last published ${label}`;

  return (
    <Tooltip
      title={
        checked
          ? `We check ${source} every day. This is the most recent rate they have published, so a gap means they have not published since, not that the feed has stopped.`
          : ''
      }
    >
      <Typography
        component={component}
        sx={{ fontSize: '0.62rem', color: palette.textMuted, fontStyle: 'italic', letterSpacing: '0.02em', ...sx }}
      >
        {text}
      </Typography>
    </Tooltip>
  );
}

export default SourceFreshness;
